import { useEffect, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';

export interface EditScorePopoverProps {
    componentLabel: string;
    aiScore: number;
    aiScoreLabel?: string;
    aiJustification?: string;
    currentScore: number;
    scaleMin: number;
    scaleMax: number;
    scaleLabels?: string[];
    binary?: boolean;
    saving?: boolean;
    onCancel: () => void;
    onSave: (editedScore: number, justification: string) => Promise<void> | void;
}

const MIN_JUSTIFICATION = 5;

export function EditScorePopover({
    componentLabel,
    aiScore,
    aiScoreLabel,
    aiJustification,
    currentScore,
    scaleMin,
    scaleMax,
    scaleLabels,
    binary,
    saving,
    onCancel,
    onSave,
}: EditScorePopoverProps) {
    const [score, setScore] = useState<number>(currentScore);
    const [justification, setJustification] = useState('');
    const [error, setError] = useState<string | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    useEffect(() => {
        textareaRef.current?.focus();
    }, []);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onCancel();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onCancel]);

    const buttons: number[] = [];
    for (let i = scaleMin; i <= scaleMax; i++) buttons.push(i);

    const submit = async () => {
        const trimmed = justification.trim();
        if (trimmed.length < MIN_JUSTIFICATION) {
            setError(`Justification needs at least ${MIN_JUSTIFICATION} characters.`);
            return;
        }
        if (score < scaleMin || score > scaleMax) {
            setError(`Score must be between ${scaleMin} and ${scaleMax}.`);
            return;
        }
        setError(null);
        await onSave(score, trimmed);
    };

    return (
        <div
            role="dialog"
            aria-label={`Editing ${componentLabel}`}
            className="rounded-lg p-4 mt-3 flex flex-col gap-3"
            style={{
                background: 'linear-gradient(180deg, var(--accent-subtle) 0%, var(--surface-primary) 100%)',
                border: '1px solid var(--border-accent)',
                boxShadow: 'var(--shadow-md, 0 8px 24px rgba(0,0,0,0.08))',
            }}
        >
            <div
                className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em]"
                style={{ color: 'var(--accent-emphasis)' }}
            >
                <Sparkles size={11} strokeWidth={2} />
                Editing: {componentLabel}
            </div>

            {/* Score selector */}
            <div className="flex flex-col gap-1.5">
                <span className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                    {binary ? 'Pass / Fail' : 'Score'}
                </span>
                <div className="flex flex-wrap gap-1.5">
                    {buttons.map((value) => {
                        const active = value === score;
                        const label = binary
                            ? value === 1
                                ? 'Pass'
                                : 'Fail'
                            : scaleLabels?.[value - scaleMin] ?? String(value);
                        return (
                            <button
                                key={value}
                                onClick={() => setScore(value)}
                                className="h-8 px-3 rounded-md text-[12px] font-medium transition-all min-w-[44px]"
                                style={{
                                    background: active ? 'var(--accent)' : 'var(--surface-primary)',
                                    color: active ? 'white' : 'var(--text-secondary)',
                                    border: `1.5px solid ${active ? 'var(--accent)' : 'var(--border-default)'}`,
                                }}
                            >
                                {binary ? label : (
                                    <>
                                        <span className="tabular-nums">{value}</span>
                                        {scaleLabels?.[value - scaleMin] && (
                                            <span className="ml-1 opacity-80">{scaleLabels[value - scaleMin]}</span>
                                        )}
                                    </>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Justification */}
            <div className="flex flex-col gap-1">
                <label
                    className="text-[11px]"
                    style={{ color: 'var(--text-tertiary)' }}
                    htmlFor="edit-score-justification"
                >
                    Why this score? <span style={{ color: 'var(--fg-danger)' }}>*</span>
                </label>
                <textarea
                    id="edit-score-justification"
                    ref={textareaRef}
                    rows={3}
                    value={justification}
                    onChange={(e) => setJustification(e.target.value)}
                    placeholder="What did the AI miss or overweight? (e.g. 'Maya from Crypto.com had the same profile and crushed it.')"
                    className="w-full rounded-md px-3 py-2 resize-none text-[12.5px] outline-none"
                    style={{
                        background: 'var(--surface-primary)',
                        color: 'var(--text-primary)',
                        border: '1px solid var(--border-default)',
                    }}
                />
            </div>

            {/* AI's own take */}
            {(aiScoreLabel || aiJustification) && (
                <div
                    className="rounded-md px-3 py-2"
                    style={{ background: 'var(--surface-secondary)', border: '1px solid var(--border-muted)' }}
                >
                    <div className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                        AI scored{' '}
                        <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                            {binary ? (aiScore === 1 ? 'Pass' : 'Fail') : aiScore}
                        </span>
                        {aiScoreLabel ? ` — ${aiScoreLabel}` : ''}
                    </div>
                    {aiJustification && (
                        <div className="text-[12px] mt-1 italic" style={{ color: 'var(--text-secondary)' }}>
                            "{aiJustification}"
                        </div>
                    )}
                </div>
            )}

            {error && (
                <div className="text-[11.5px]" style={{ color: 'var(--fg-danger)' }}>
                    {error}
                </div>
            )}

            <div className="flex gap-2 justify-end">
                <button
                    onClick={onCancel}
                    disabled={saving}
                    className="h-8 px-3 rounded-md text-[12px]"
                    style={{
                        background: 'var(--surface-primary)',
                        color: 'var(--text-secondary)',
                        border: '1px solid var(--border-default)',
                    }}
                >
                    Cancel
                </button>
                <button
                    onClick={() => void submit()}
                    disabled={saving || justification.trim().length < MIN_JUSTIFICATION}
                    className="h-8 px-3 rounded-md text-[12px] font-medium disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                    style={{
                        background: 'var(--accent)',
                        color: 'white',
                    }}
                >
                    <Sparkles size={11} strokeWidth={2} />
                    {saving ? 'Saving…' : 'Save & Add to Brain'}
                </button>
            </div>
        </div>
    );
}
